import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Goals from './pages/Goals'
import WorkLogs from './pages/WorkLogs'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import Login from './pages/Login'
import Admin from './pages/Admin'
import Timeline from './pages/Timeline'
import MonthlySummary from './pages/MonthlySummary'
import { checkAuth, handleCallback, getCurrentUser } from './utils/auth'
import { fetchMeta, fetchSession } from './utils/api'

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [ownerId, setOwnerId] = useState('')
  const [isOwner, setIsOwner] = useState(false)
  const [isTeamMember, setIsTeamMember] = useState(false)
  const [metaLoaded, setMetaLoaded] = useState(false)

  useEffect(() => {
    const initAuth = async () => {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')

      if (code) {
        try {
          await handleCallback(code)
          setIsAuthenticated(true)
          // 清理URL中的code参数，避免重复处理
          window.history.replaceState(null, '', window.location.pathname)
          return
        } catch (error) {
          console.error('企业微信登录失败:', error)
          alert('企业微信登录失败，请重试')
          setIsAuthenticated(false)
          return
        }
      }

      const auth = await checkAuth()
      setIsAuthenticated(auth)
    }

    initAuth()
  }, [])

  useEffect(() => {
    const loadMeta = async () => {
      if (!isAuthenticated) {
        setMetaLoaded(true)
        setIsOwner(false)
        return
      }
      try {
        const [meta, session] = await Promise.all([fetchMeta(), fetchSession()])
        const fallbackOwner = meta?.ownerId || ''
        const ownerFromSession = session?.ownerId || ''
        const currentUser = getCurrentUser()
        const nextOwnerId = ownerFromSession || fallbackOwner
        setOwnerId(nextOwnerId)
        const isOwnerBySession = Boolean(session?.isOwner)
        const isOwnerById = Boolean(nextOwnerId && currentUser.userId && nextOwnerId.toLowerCase() === currentUser.userId.toLowerCase())
        const ownerFlag = isOwnerBySession || isOwnerById
        setIsOwner(ownerFlag)
        // 团队成员包含owner本身；若本地已有 userId，也视为团队成员（企业微信 cookie/localStorage 兜底）
        setIsTeamMember(ownerFlag || Boolean(session?.isTeamMember) || Boolean(currentUser.userId))
      } catch {
        try {
          const meta = await fetchMeta()
          setOwnerId(meta?.ownerId || '')
        } catch {
          setOwnerId('')
        }
        setIsOwner(false)
      } finally {
        setMetaLoaded(true)
      }
    }
    loadMeta()
  }, [isAuthenticated, ownerId])

  if (isAuthenticated === null || (isAuthenticated && !metaLoaded)) {
    return <div className="loading">检查登录状态...</div>
  }

  return (
    <BrowserRouter basename="/okr">
      {isAuthenticated ? (
        <Layout isOwner={isOwner}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/goals" element={<Goals isTeamMember={isTeamMember} />} />
            <Route path="/worklogs" element={<WorkLogs />} />
            <Route path="/reports" element={<Reports isOwner={isOwner} />} />
            <Route path="/timeline" element={<Timeline />} />
            <Route path="/monthly-summary" element={<MonthlySummary />} />
            <Route path="/settings" element={isOwner ? <Settings /> : <Navigate to="/" replace />} />
            <Route path="/admin" element={isOwner ? <Admin /> : <Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      ) : (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </BrowserRouter>
  )
}

export default App
